import { useLicensingServiceV1 } from "@rw1/api-services";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useParams } from "react-router-dom";
import { toHistoricalSeatAllocation } from "../../../domain/mappers/licenseMappers";
import type { HistoricalSeatAllocation } from "../../../domain/models/license";
import { GC_TIME_MS, STALE_TIME_MS } from "../../../hooks/constants";
import { useCompaniesData } from "../../../hooks/useCompaniesData";
import type { SeatHistoryEntry } from "./types";

export const SEAT_HISTORY_QUERY_KEY = "seatHistory";

interface UseSeatHistoryParams {
  startDate: Date;
  endDate: Date;
  enabled?: boolean;
}

interface UseSeatHistoryResult {
  data: SeatHistoryEntry[];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
}

const PAGE_SIZE = 500;

const toSeatHistoryEntry = (
  alloc: HistoricalSeatAllocation,
  companyNamesMap: Record<string, string>,
): SeatHistoryEntry => ({
  id: alloc.licenseAllocationId,
  grantedViaLicense: `${companyNamesMap[alloc.assignedOrganizationId] ?? "Unknown Company"} - ${alloc.licenseName}`,
  anonymizedUser: alloc.userId,
  dateAssigned: alloc.createdAt,
  currentExpiryDate: alloc.expiresAt,
  originalExpiryDate: alloc.originalExpiresAt,
});

export const useSeatHistory = ({
  startDate,
  endDate,
  enabled: externalEnabled = true,
}: UseSeatHistoryParams): UseSeatHistoryResult => {
  const { companyId } = useParams();
  const licensingService = useLicensingServiceV1();

  const {
    companyNamesByIdMap,
    companiesLoading,
    isFetching: isCompaniesFetching,
  } = useCompaniesData();

  const { data, error, isLoading, isFetching } = useQuery({
    queryKey: [
      SEAT_HISTORY_QUERY_KEY,
      companyId,
      startDate.toISOString(),
      endDate.toISOString(),
    ],
    queryFn: async (): Promise<HistoricalSeatAllocation[]> => {
      if (!companyId) {
        throw new Error("Company ID is required");
      }

      const allAllocations: HistoricalSeatAllocation[] = [];
      let page = 0;
      let batch: HistoricalSeatAllocation[];

      do {
        batch = (
          await licensingService.getHistoricalUserAllocationsByCompanyId(
            companyId,
            page,
            PAGE_SIZE,
          )
        ).map(toHistoricalSeatAllocation);
        allAllocations.push(...batch);
        page++;
      } while (batch.length === PAGE_SIZE);

      return allAllocations.filter(
        (a) =>
          // Interval-overlap: seat was active at any point during [startDate, endDate].
          // Assigned before the range ends AND (no expiry set, or expires after range starts).
          a.createdAt <= endDate &&
          (a.expiresAt === undefined || a.expiresAt >= startDate),
      );
    },
    enabled: !!companyId && externalEnabled,
    staleTime: STALE_TIME_MS,
    gcTime: GC_TIME_MS,
    placeholderData: keepPreviousData,
    retry: 1,
  });

  const mappedData = useMemo(() => {
    if (!data) return [];

    return data
      .map((a) => toSeatHistoryEntry(a, companyNamesByIdMap))
      .sort((a, b) => b.dateAssigned.getTime() - a.dateAssigned.getTime());
  }, [data, companyNamesByIdMap]);

  return {
    data: mappedData,
    isLoading: isLoading || companiesLoading,
    isFetching: isFetching || isCompaniesFetching,
    error: error ?? null,
  };
};
